import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Hash, Plus, MessageSquare, Settings, Shield, Menu, Search, Lock, Users,
  Link2, Check, X, Bell, Bookmark, Phone, Bot, Boxes, Wallet, UserPlus, ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { channelsApi } from '@/api/channels';
import { useAuthStore } from '@/store/authStore';
import { useChatStore } from '@/store/chatStore';
import Avatar from '@/components/ui/Avatar';
import CreateChannelModal from './CreateChannelModal';
import CreateSecretChatModal from './CreateSecretChatModal';
import { secretChatsApi } from '@/api/secretChats';
import { useMobile } from '@/pages/chat/ChatPage';
import { useSidebarPanelStore } from '@/store/sidebarPanelStore';
import { useKarmaStore } from '@/store/karmaStore';
import NickName from '@/components/ui/NickName';

export default function ChannelSidebar() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { activeChannelId, setActiveChannel, joinedChannels, addJoinedChannel, unreadCounts } = useChatStore();
  const { setShowSidebar } = useMobile();
  // Phase 21.3 redesign — вкладка теперь общая с TopBar.
  const panel = useSidebarPanelStore((s) => s.panel);
  const setPanel = useSidebarPanelStore((s) => s.setPanel);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSecretOpen, setCreateSecretOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [inviteModal, setInviteModal] = useState<string | null>(null); // channelId
  const [requestsModal, setRequestsModal] = useState<string | null>(null); // channelId
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  // Выпадающее меню "надстройки" под кнопкой-гамбургером (Create channel /
  // Library / Calls / Bots / Mini-apps / Admin / Settings).
  const [menuOpen, setMenuOpen] = useState(false);

  const { data: publicChannels } = useQuery({
    queryKey: ['channels', 'public', search],
    queryFn: () => channelsApi.list({ limit: 30, search: search || undefined }),
    enabled: panel === 'browse' && search.length >= 1,
  });

  const { data: secretChats } = useQuery({
    queryKey: ['secret-chats'],
    queryFn: () => secretChatsApi.list(),
    enabled: panel === 'secret',
    refetchInterval: panel === 'secret' ? 15_000 : false,
  });

  const isAdmin = user?.globalRole === 'ADMIN' || user?.globalRole === 'SUPER_ADMIN';

  // Load karma map for SUPER_ADMIN (silent, invisible to others)
  const karmaLoad = useKarmaStore((s) => s.load);
  useEffect(() => {
    if (user?.globalRole === 'SUPER_ADMIN') karmaLoad();
  }, [user?.globalRole, karmaLoad]);

  const navigateToChannel = (channelId: string) => {
    setActiveChannel(channelId);
    navigate(`/channel/${channelId}`);
    setShowSidebar(false); // hide sidebar on mobile
  };

  return (
    <aside className="w-full md:w-60 bg-surface-900 border-r border-surface-800 flex flex-col flex-shrink-0 h-full">
      {/* Mini-header. Tabs/stories/search/notifications/logout moved to TopBar (Phase 21.3). */}
      <div className="h-12 px-3 flex items-center gap-1.5 border-b border-surface-800 flex-shrink-0 relative">
        <button onClick={() => setMenuOpen((v) => !v)} title="Меню"
          className={clsx(
            'w-7 h-7 rounded-full flex items-center justify-center transition-colors flex-shrink-0',
            menuOpen
              ? 'bg-brand-600 text-white'
              : 'bg-surface-800 text-surface-400 hover:bg-surface-700 hover:text-surface-200',
          )}>
          <Menu size={14} />
        </button>

        <span className="text-xs text-surface-500 uppercase tracking-wider flex-1 truncate pl-1">
          {panel === 'channels' ? 'Каналы'
            : panel === 'dm' ? 'Диалоги'
            : panel === 'secret' ? 'Тунели'
            : 'Поиск каналов'}
        </span>

        {menuOpen && (
          <>
            {/* invisible backdrop to dismiss on outside click */}
            <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
            <div className="absolute left-2 top-11 z-40 w-56 bg-surface-900 border border-surface-700 rounded-xl shadow-2xl py-1.5">
              <button onClick={() => { setMenuOpen(false); setCreateOpen(true); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800 transition-colors">
                <Plus size={14} className="text-brand-400" /> Создать канал
              </button>
              <button onClick={() => { setMenuOpen(false); navigate('/library'); setShowSidebar(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800 transition-colors">
                <Bookmark size={14} className="text-surface-400" /> Библиотека
              </button>
              <button onClick={() => { setMenuOpen(false); navigate('/calls'); setShowSidebar(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800 transition-colors">
                <Phone size={14} className="text-surface-400" /> История звонков
              </button>
              <button onClick={() => { setMenuOpen(false); navigate('/bots'); setShowSidebar(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800 transition-colors">
                <Bot size={14} className="text-surface-400" /> Мои боты
              </button>
              <button onClick={() => { setMenuOpen(false); navigate('/miniapps'); setShowSidebar(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800 transition-colors">
                <Boxes size={14} className="text-surface-400" /> Мини-аппы
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  navigate('/billing');
                  setShowSidebar(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800 transition-colors">
                <Wallet size={14} className="text-yellow-400" /> Пополнить SWIPA
              </button>
              {isAdmin && (
                <button onClick={() => { setMenuOpen(false); navigate('/admin'); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800 transition-colors">
                  <Shield size={14} className="text-surface-400" /> Админ панель
                </button>
              )}
              <div className="h-px bg-surface-800 my-1" />
              <button onClick={() => { setMenuOpen(false); navigate('/settings'); setShowSidebar(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-200 hover:bg-surface-800 transition-colors">
                <Settings size={14} className="text-surface-400" /> Настройки
              </button>
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto py-1">

        {/* My channels */}
        {panel === 'channels' && (
          <>
            {/* Search channels — mobile inline, desktop uses browse tab */}
            <div className="md:hidden px-2 py-2">
              <div className="flex items-center gap-2 bg-surface-800 border border-surface-700 rounded-lg px-2.5 py-2">
                <Search size={14} className="text-surface-500 flex-shrink-0" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Поиск каналов..."
                  className="bg-transparent text-sm text-surface-200 outline-none w-full placeholder:text-surface-600"
                />
                {search.length > 0 && (
                  <button onClick={() => setSearch('')} className="text-surface-500"><X size={14} /></button>
                )}
              </div>
            </div>
            {/* If searching on mobile — show browse results */}
            {search.length > 0 ? (
              <>
                {publicChannels?.data.length === 0 && (
                  <p className="px-3 py-3 text-surface-500 text-xs">Каналы не найдены</p>
                )}
                {publicChannels?.data.map((ch) => {
                  const isJoined = joinedChannels.some((jc) => jc.id === ch.id);
                  const memberCount = ch._count?.members;
                  return (
                    <div key={ch.id} className="flex items-center gap-2 px-3 py-3 md:py-2 text-sm text-surface-300 hover:bg-surface-800 transition-colors">
                      {ch.type === 'PRIVATE' ? <Lock size={14} className="text-surface-500 flex-shrink-0" /> : <Hash size={14} className="text-surface-500 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="truncate">{ch.displayName || ch.name}</p>
                        {memberCount !== undefined && (
                          <p className="text-[10px] text-surface-600 flex items-center gap-0.5"><Users size={9} />{memberCount}</p>
                        )}
                      </div>
                      {isJoined ? (
                        <button onClick={() => { navigateToChannel(ch.id); setSearch(''); }}
                          className="text-xs text-brand-400 flex-shrink-0">Открыть</button>
                      ) : ch.type === 'PRIVATE' ? (
                        <button onClick={async () => { const res = await channelsApi.requestJoin(ch.id); if (res.status === 'pending') alert('Заявка отправлена'); }}
                          className="text-xs text-yellow-400 flex-shrink-0">Запросить</button>
                      ) : (
                        <button onClick={async () => { await channelsApi.join(ch.id); addJoinedChannel(ch); navigateToChannel(ch.id); setSearch(''); }}
                          className="text-xs text-green-400 flex-shrink-0">Вступить</button>
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                {joinedChannels.length === 0 && (
                  <p className="px-3 py-3 text-surface-500 text-xs">
                    Вы не состоите ни в одном канале. Введите название в поиске.
                  </p>
                )}
                {/* Personal "Сохранённые" channel shown FIRST with bookmark icon */}
                {(joinedChannels ?? []).filter((c) => c.isPersonal).map((ch) => {
                  const unread = unreadCounts[ch.id] ?? 0;
                  return (
                    <button
                      key={ch.id}
                      onClick={() => navigateToChannel(ch.id)}
                      className={clsx('sidebar-item w-full py-3 md:py-1.5',
                        activeChannelId === ch.id && 'sidebar-item-active')}>
                      <Bookmark size={14} className="flex-shrink-0 text-brand-400" />
                      <span className="truncate flex-1 text-sm md:text-xs">Сохранённые</span>
                      {unread > 0 && (
                        <span className="badge bg-brand-600 text-white ml-auto">
                          {unread > 99 ? '99+' : unread}
                        </span>
                      )}
                    </button>
                  );
                })}
                {(joinedChannels ?? []).filter((c) => !c.isPersonal).map((ch) => {
                  const unread = unreadCounts[ch.id] ?? 0;
                  const memberCount = ch._count?.members;
                  return (
                    <div key={ch.id} className="flex items-center group">
                      <button
                        onClick={() => navigateToChannel(ch.id)}
                        className={clsx('sidebar-item flex-1 py-3 md:py-1.5',
                          activeChannelId === ch.id && 'sidebar-item-active')}>
                        {ch.type === 'PRIVATE' ? <Lock size={14} className="flex-shrink-0" /> : <Hash size={16} className="flex-shrink-0" />}
                        <span className="truncate flex-1 text-sm md:text-xs">{ch.displayName || ch.name}</span>
                        {unread > 0 ? (
                          <span className="badge bg-brand-600 text-white ml-auto">
                            {unread > 99 ? '99+' : unread}
                          </span>
                        ) : memberCount !== undefined ? (
                          <span className="text-[10px] text-surface-600 ml-auto flex items-center gap-0.5 flex-shrink-0">
                            <Users size={9} />
                            {memberCount > 999 ? `${Math.floor(memberCount / 1000)}k` : memberCount}
                          </span>
                        ) : null}
                      </button>
                      <div className="hidden group-hover:flex items-center gap-0.5 pr-1">
                        <button onClick={(e) => { e.stopPropagation(); setInviteModal(ch.id); }} title="Пригласить"
                          className="w-6 h-6 rounded flex items-center justify-center text-surface-500 hover:text-brand-400 hover:bg-surface-800">
                          <Link2 size={12} />
                        </button>
                        {ch.type === 'PRIVATE' && (
                          <button onClick={(e) => { e.stopPropagation(); setRequestsModal(ch.id); }} title="Заявки"
                            className="w-6 h-6 rounded flex items-center justify-center text-surface-500 hover:text-yellow-400 hover:bg-surface-800">
                            <Bell size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}

        {/* DM — merged: friends + bots + open-all */}
        {panel === 'dm' && <DmPanelContent navigate={navigate} setShowSidebar={setShowSidebar} />}

        {/* Secret chats */}
        {panel === 'secret' && (
          <>
            <button
              onClick={() => setCreateSecretOpen(true)}
              className="sidebar-item w-full text-left text-brand-300"
            >
              <Plus size={15} />
              <span>Новый тунель</span>
            </button>
            {secretChats && secretChats.length === 0 && (
              <p className="px-3 py-3 text-surface-500 text-xs leading-relaxed">
                Тунели сквозно зашифрованы и не отображаются в обычных
                диалогах. Сообщения хранятся только на устройствах участников.
              </p>
            )}
            {secretChats?.map((sc) => {
              const otherId = sc.initiatorId === user?.id ? sc.responderId : sc.initiatorId;
              return (
                <button
                  key={sc.id}
                  onClick={() => { setShowSidebar(false); navigate(`/secret/${sc.id}`); }}
                  className="sidebar-item w-full text-left"
                >
                  <Lock size={13} className="text-brand-400 flex-shrink-0" />
                  <span className="truncate flex-1 font-mono text-xs">
                    {otherId.slice(0, 8)}…
                  </span>
                  {sc.selfDestructSeconds > 0 && (
                    <span className="text-[9px] text-surface-600 flex-shrink-0">⏱</span>
                  )}
                </button>
              );
            })}
          </>
        )}

        {/* Friends panel removed — merged into Диалоги (Phase 26.1). */}

        {/* Browse */}
        {panel === 'browse' && (
          <>
            <div className="px-2 py-2">
              <div className="flex items-center gap-2 bg-surface-800 border border-surface-700 rounded-lg px-2 py-1.5">
                <Search size={13} className="text-surface-500 flex-shrink-0" />
                <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Введите название..."
                  className="bg-transparent text-sm text-surface-200 outline-none w-full" />
              </div>
            </div>
            {search.length === 0 && (
              <p className="px-3 text-surface-500 text-xs">Начните вводить название канала</p>
            )}
            {search.length >= 1 && publicChannels?.data.length === 0 && (
              <p className="px-3 text-surface-500 text-xs">Каналы не найдены</p>
            )}
            {publicChannels?.data.map((ch) => {
              const isJoined = joinedChannels.some((jc) => jc.id === ch.id);
              const memberCount = ch._count?.members;
              return (
                <div key={ch.id} className="flex items-center gap-2 px-3 py-2 text-sm text-surface-300 hover:bg-surface-800 transition-colors">
                  {ch.type === 'PRIVATE' ? <Lock size={13} className="text-surface-500 flex-shrink-0" /> : <Hash size={13} className="text-surface-500 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="truncate">{ch.displayName || ch.name}</p>
                    {memberCount !== undefined && (
                      <p className="text-[10px] text-surface-600 flex items-center gap-0.5">
                        <Users size={9} />{memberCount} участников
                      </p>
                    )}
                  </div>
                  {isJoined ? (
                    <button onClick={() => { navigateToChannel(ch.id); setPanel('channels'); }}
                      className="text-xs text-brand-400 hover:text-brand-300 flex-shrink-0">
                      Открыть
                    </button>
                  ) : ch.type === 'PRIVATE' ? (
                    <button onClick={async () => {
                        const res = await channelsApi.requestJoin(ch.id);
                        if (res.status === 'pending') {
                          alert('Заявка на вступление отправлена. Ожидайте подтверждения.');
                        }
                      }}
                      className="text-xs text-yellow-400 hover:text-yellow-300 flex-shrink-0">
                      Запросить
                    </button>
                  ) : (
                    <button onClick={async () => {
                        await channelsApi.join(ch.id);
                        addJoinedChannel(ch);
                        navigateToChannel(ch.id);
                        setPanel('channels');
                      }}
                      className="text-xs text-green-400 hover:text-green-300 flex-shrink-0">
                      Вступить
                    </button>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* User footer — hidden on mobile (bottom nav handles navigation) */}
      <div
        className="hidden md:flex border-t border-surface-800 p-3 items-center gap-2 flex-shrink-0 cursor-pointer hover:bg-surface-800 transition-colors"
        onClick={() => navigate(`/u/${user?.username}`)}
      >
        <Avatar src={user?.avatarUrl} username={user?.username ?? '?'} size="sm" status={user?.status} />
        <div className="flex-1 min-w-0">
          <p className="text-surface-100 text-xs font-semibold truncate"><NickName user={user ?? {}} fallback="?" /></p>
          <p className="text-surface-500 text-xs truncate font-mono">@{user?.username}</p>
        </div>
      </div>

      <CreateChannelModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <CreateSecretChatModal open={createSecretOpen} onClose={() => setCreateSecretOpen(false)} />

      {/* Invite modal */}
      {inviteModal && (
        <InviteModal channelId={inviteModal} onClose={() => { setInviteModal(null); setCopiedCode(null); }} copiedCode={copiedCode} setCopiedCode={setCopiedCode} />
      )}

      {/* Join requests modal */}
      {requestsModal && (
        <JoinRequestsModal channelId={requestsModal} onClose={() => setRequestsModal(null)} />
      )}
    </aside>
  );
}

function InviteModal({ channelId, onClose, copiedCode, setCopiedCode }: {
  channelId: string;
  onClose: () => void;
  copiedCode: string | null;
  setCopiedCode: (v: string | null) => void;
}) {
  const queryClient = useQueryClient();

  const { data: invites, isLoading } = useQuery({
    queryKey: ['invites', channelId],
    queryFn: () => channelsApi.listInvites(channelId),
  });

  const createMutation = useMutation({
    mutationFn: () => channelsApi.createInvite(channelId, { maxUses: 0 }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invites', channelId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => channelsApi.deleteInvite(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invites', channelId] }),
  });

  const copyLink = (code: string) => {
    const url = `${window.location.origin}/invite/${code}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-surface-900 border border-surface-800 rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-surface-100 font-semibold mb-4">Приглашения в канал</h3>

        {createMutation.error && (
          <div className="bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-lg px-3 py-2 mb-3">
            {(createMutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Недостаточно прав для создания приглашений'}
          </div>
        )}

        <button onClick={() => createMutation.mutate()} disabled={createMutation.isPending}
          className="btn-primary w-full mb-4">
          {createMutation.isPending ? 'Создание...' : 'Создать ссылку-приглашение'}
        </button>

        {isLoading && <p className="text-surface-500 text-sm">Загрузка...</p>}

        {invites && invites.length === 0 && (
          <p className="text-surface-500 text-sm text-center">Нет активных приглашений</p>
        )}

        <div className="space-y-2 max-h-60 overflow-y-auto">
          {invites?.map((inv) => (
            <div key={inv.id} className="flex items-center gap-2 bg-surface-800 rounded-lg px-3 py-2">
              <code className="text-xs text-surface-300 truncate flex-1 font-mono">{inv.code}</code>
              <span className="text-xs text-surface-500">{inv.uses} исп.</span>
              <button onClick={() => copyLink(inv.code)} title="Копировать ссылку"
                className="w-7 h-7 rounded flex items-center justify-center text-surface-400 hover:text-brand-400 hover:bg-surface-700">
                {copiedCode === inv.code ? <Check size={14} className="text-green-400" /> : <Link2 size={14} />}
              </button>
              <button onClick={() => deleteMutation.mutate(inv.id)} title="Удалить"
                className="w-7 h-7 rounded flex items-center justify-center text-surface-400 hover:text-red-400 hover:bg-surface-700">
                <X size={14} />
              </button>
            </div>
          ))}
        </div>

        <button onClick={onClose} className="btn-ghost w-full mt-4">Закрыть</button>
      </div>
    </div>
  );
}

function JoinRequestsModal({ channelId, onClose }: { channelId: string; onClose: () => void }) {
  const queryClient = useQueryClient();

  const { data: requests, isLoading } = useQuery({
    queryKey: ['join-requests', channelId],
    queryFn: () => channelsApi.listJoinRequests(channelId),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => channelsApi.approveJoinRequest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['join-requests', channelId] });
      queryClient.invalidateQueries({ queryKey: ['channels'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id: string) => channelsApi.rejectJoinRequest(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['join-requests', channelId] }),
  });

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-surface-900 border border-surface-800 rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-surface-100 font-semibold mb-4">Заявки на вступление</h3>

        {isLoading && <p className="text-surface-500 text-sm">Загрузка...</p>}

        {requests && requests.length === 0 && (
          <p className="text-surface-500 text-sm text-center py-4">Нет ожидающих заявок</p>
        )}

        <div className="space-y-3 max-h-80 overflow-y-auto">
          {requests?.map((req) => (
            <div key={req.id} className="bg-surface-800 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full bg-brand-700 flex items-center justify-center text-xs font-bold text-white flex-shrink-0">
                  {req.user.username.slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-surface-100 text-sm font-medium truncate"><NickName user={req.user} /></p>
                  <p className="text-surface-500 text-xs font-mono">@{req.user.username}</p>
                </div>
              </div>
              {req.message && (
                <p className="text-surface-400 text-xs mb-2 italic">"{req.message}"</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => approveMutation.mutate(req.id)}
                  disabled={approveMutation.isPending}
                  className="flex-1 py-1.5 rounded-lg bg-green-700 hover:bg-green-600 text-white text-xs font-medium transition-colors">
                  Принять
                </button>
                <button
                  onClick={() => rejectMutation.mutate(req.id)}
                  disabled={rejectMutation.isPending}
                  className="flex-1 py-1.5 rounded-lg bg-red-700 hover:bg-red-600 text-white text-xs font-medium transition-colors">
                  Отклонить
                </button>
              </div>
            </div>
          ))}
        </div>

        <button onClick={onClose} className="btn-ghost w-full mt-4">Закрыть</button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Phase 26.1 — DM panel content: друзья + свои боты + входящие заявки + поиск.
// Раньше это был отдельный tab «Друзья», теперь всё в «Диалоги».
// ────────────────────────────────────────────────────────────────────────────
function DmPanelContent({
  navigate,
  setShowSidebar,
}: {
  navigate: (p: string) => void;
  setShowSidebar: (v: boolean) => void;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const { data: friends } = useQuery({
    queryKey: ['friends'],
    queryFn: () => import('@/api/friends').then((m) => m.friendsApi.list()),
  });
  const { data: pendingRequests } = useQuery({
    queryKey: ['friends', 'pending'],
    queryFn: () => import('@/api/friends').then((m) => m.friendsApi.listPending()),
    staleTime: 30_000,
  });
  const { data: myBots } = useQuery({
    queryKey: ['my-bots'],
    queryFn: () => import('@/api/bots').then((m) => m.botsApi.list()),
    staleTime: 60_000,
  });

  const acceptMut = useMutation({
    mutationFn: (id: string) => import('@/api/friends').then((m) => m.friendsApi.accept(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['friends'] }),
  });
  const rejectMut = useMutation({
    mutationFn: (id: string) => import('@/api/friends').then((m) => m.friendsApi.reject(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['friends'] }),
  });

  const q = search.toLowerCase();
  const filterFn = (name: string, username: string) =>
    !q || name.toLowerCase().includes(q) || username.toLowerCase().includes(q);

  const visibleFriends = (friends ?? []).filter((f: any) =>
    filterFn(f.friend.displayName ?? '', f.friend.username ?? ''),
  );
  const visibleBots = (myBots ?? []).filter((b: any) =>
    filterFn(b.displayName ?? '', b.username ?? ''),
  );

  return (
    <>
      {/* Search */}
      <div className="px-2 py-2">
        <div className="flex items-center gap-2 bg-surface-800 border border-surface-700 rounded-lg px-2.5 py-1.5">
          <Search size={13} className="text-surface-500 flex-shrink-0" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Поиск диалогов…"
            className="bg-transparent text-xs text-surface-200 outline-none w-full placeholder:text-surface-500"
          />
          {search.length > 0 && (
            <button onClick={() => setSearch('')} className="text-surface-500">
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={() => {
          setShowSidebar(false);
          navigate('/friends/requests');
        }}
        className="mx-2 mb-1 flex min-h-11 w-[calc(100%-1rem)] items-center gap-2 rounded-lg px-2 text-left text-surface-300 transition-colors hover:bg-surface-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-brand-900/40 text-brand-300">
          <UserPlus size={15} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-xs font-medium text-surface-200">Заявки в друзья</span>
          <span className="block text-[10px] text-surface-500">Входящие и исходящие</span>
        </span>
        {pendingRequests && pendingRequests.length > 0 && (
          <span className="min-w-5 rounded-full bg-red-600 px-1.5 py-0.5 text-center text-[10px] text-white">
            {pendingRequests.length > 99 ? '99+' : pendingRequests.length}
          </span>
        )}
        <ChevronRight size={15} className="text-surface-600" />
      </button>

      {/* Incoming friend requests */}
      {pendingRequests && pendingRequests.length > 0 && (
        <div className="px-2 py-2">
          <p className="text-[10px] uppercase tracking-wider text-surface-500 px-1 mb-1">
            Заявки ({pendingRequests.length})
          </p>
          {pendingRequests.map((req: any) => (
            <div key={req.id} className="flex items-center gap-2 py-1.5 px-1">
              <Avatar src={req.sender.avatarUrl} username={req.sender.username} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="text-surface-200 text-xs truncate">
                  <NickName user={req.sender} />
                </p>
                <p className="text-surface-500 text-[10px] font-mono truncate">
                  @{req.sender.username}
                </p>
              </div>
              <button
                onClick={() => acceptMut.mutate(req.id)}
                className="w-6 h-6 rounded flex items-center justify-center text-green-400 hover:bg-surface-800"
                title="Принять"
              >
                <Check size={12} />
              </button>
              <button
                onClick={() => rejectMut.mutate(req.id)}
                className="w-6 h-6 rounded flex items-center justify-center text-red-400 hover:bg-surface-800"
                title="Отклонить"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* My bots */}
      {visibleBots.length > 0 && (
        <>
          <p className="text-[10px] uppercase tracking-wider text-surface-500 px-3 pt-2 pb-1">
            Мои боты
          </p>
          {visibleBots.map((bot: any) => (
            <button
              key={bot.id}
              onClick={() => {
                setShowSidebar(false);
                navigate(`/dm/${bot.userId}`);
              }}
              className="sidebar-item w-full text-left py-2 md:py-1.5"
            >
              <div className="relative flex-shrink-0">
                <Avatar src={bot.avatarUrl} username={bot.username} size="sm" />
                <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-brand-600 border border-surface-900 flex items-center justify-center">
                  <Bot size={8} className="text-white" />
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <span className="truncate block text-xs text-surface-200">{bot.displayName}</span>
                <span className="truncate block text-[10px] text-surface-500 font-mono">
                  @{bot.username}
                </span>
              </div>
            </button>
          ))}
        </>
      )}

      {/* Friends */}
      {visibleFriends.length > 0 && (
        <>
          <p className="text-[10px] uppercase tracking-wider text-surface-500 px-3 pt-2 pb-1">
            Друзья
          </p>
          {visibleFriends.map((f: any) => (
            <button
              key={f.friendshipId}
              onClick={() => {
                setShowSidebar(false);
                navigate(`/dm/${f.friend.id}`);
              }}
              className="sidebar-item w-full text-left py-2 md:py-1.5"
            >
              <Avatar
                src={f.friend.avatarUrl}
                username={f.friend.username}
                size="sm"
                status={f.friend.status as any}
              />
              <div className="flex-1 min-w-0">
                <span className="truncate block text-xs text-surface-200">
                  <NickName user={f.friend} />
                </span>
                <span className="truncate block text-[10px] text-surface-500 font-mono">
                  @{f.friend.username}
                </span>
              </div>
            </button>
          ))}
        </>
      )}

      {/* Open all conversations */}
      <button
        onClick={() => {
          setShowSidebar(false);
          navigate('/dm');
        }}
        className="sidebar-item w-full text-left mt-2 text-brand-300"
      >
        <MessageSquare size={13} />
        <span className="text-xs">Все диалоги…</span>
      </button>

      {visibleFriends.length === 0 && visibleBots.length === 0 && !pendingRequests?.length && (
        <p className="px-3 py-3 text-surface-500 text-xs text-center">
          Пусто. Добавьте друга или создайте бота.
        </p>
      )}
    </>
  );
}
